import { Box, Button, colors } from '@factorialco/deprecated-design-system'
import axios from 'axios'
import { format } from 'date-fns'
import domtoimage from 'dom-to-image'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { download, downloadBlob } from 'modules/core/lib/download'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type WidgetMetrics from 'modules/insights/lib/widgetMetrics'

export enum ExportType {
  Xlsx = 'xlsx',
  Csv = 'csv',
  Png = 'png',
}

type ExportForm = {
  name: string
  type: ExportType
}

type ExportModal = {
  open: boolean
  widgetMetric: WidgetMetrics
  insightsDOM: React.RefObject<HTMLElement>
  onClose: () => void
}

type downloadReportOptions = { type: ExportType; reports: unknown }
const downloadReport = ({ type, reports }: downloadReportOptions) => {
  axios({
    method: 'POST',
    url: buildUrl('/reports/export_insights'),
    withCredentials: true,
    data: { type, reports },
    responseType: 'blob',
  })
    .then((response) => {
      const contentDisposition = response.headers['content-disposition']
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      const filename = filenameMatch[1]

      downloadBlob(response.data, filename)
    })
    .catch((_error) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('insights.exports.forbidden'),
      })
    })
}

const downloadPng = (widgetContainerRef: React.RefObject<HTMLElement>) => {
  widgetContainerRef.current &&
    domtoimage
      .toPng(widgetContainerRef.current, {
        bgcolor: colors.grey000White,
      })
      .then((image) => download(image, `${getInitialFileName()}.png`))
}

const getInitialFileName = () => {
  const date = format(new Date(), 'YYYMMdd')

  return `${date}-${i18n.t('insights.section_name')}`
}

const ExportInsightsModal = ({ open, onClose, widgetMetric, insightsDOM }: ExportModal) => {
  const options = [
    {
      label: i18n.t('insights.exports.page.xlsx.type'),
      value: ExportType.Xlsx,
    },
    {
      label: i18n.t('insights.exports.page.csv.type'),
      value: ExportType.Csv,
    },
    {
      label: i18n.t('insights.exports.page.png.type'),
      value: ExportType.Png,
    },
  ]

  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()
  const form = useForm<ExportForm>({
    defaultValues: {
      name: getInitialFileName(),
      type: ExportType.Xlsx,
    },
    onSubmit: async (data) => {
      track(trackingEvents.insights.fullPageExport, {
        company_id: companyId,
        access_id: accessId,
        export_type: data.type,
      })

      switch (data.type) {
        case ExportType.Xlsx:
        case ExportType.Csv: {
          const reports = widgetMetric.export()
          downloadReport({ type: data.type, reports })
          onClose()
          break
        }
        case ExportType.Png: {
          downloadPng(insightsDOM)
          onClose()
          break
        }
        default: {
          throw 'absurd'
        }
      }

      return {
        success: true,
        data: [],
      }
    },
  })

  return (
    <>
      {open && (
        <Modal
          title={i18n.t('insights.exports.title')}
          illustration="table-file"
          onClose={() => onClose()}
          footer={
            <>
              <Button type="secondary" onClick={() => onClose()}>
                {i18n.t('common.cancel')}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  form.submitForm()
                }}
              >
                {i18n.t('common.export')}
              </Button>
            </>
          }
        >
          <Box gap="s24">
            <SelectField
              label={i18n.t('data_table.export.modal.file_format')}
              value={form.values.type}
              onChange={(value) => {
                form.setFieldValue('type', value)
              }}
              options={options}
              readonly={form.isSubmitting}
            />
          </Box>
        </Modal>
      )}
    </>
  )
}

export default ExportInsightsModal
